// @flow
import { createSelector } from 'reselect';
import { filter, first, flowRight, curry, map, identity, includes as _includes } from 'lodash/fp';
import { fromJS, Map as ImmutableMap } from 'immutable';

import { getLocales, getConfigBound } from '~/core/state/config/selectors';
import { getAppSettings, getAppSetting } from '~/core/state/app-settings/selectors';
import { getBulbs } from '~/core/state/products/bulbs/selectors';
import { getConfig } from '~/core/state/config/selectors';
import { getHubHasCapability, getHueBridge } from '~/core/state/devices/selectors';
import { getAllCookies } from '~/core/state/cookies/selectors';
import { capabilities } from '~/core/constants/devices';
import { buildKey } from '~/core/utils/app-settings';
import { SEEN_OFFER } from '~/core/constants/app-settings';
import { DISCOVER_FEATURES, DISCOVER_NEW_FEATURES } from '~/core/state/app-settings/constants';
import { dashboard as dashboardRoute } from '~/core/constants/routes';
import { isGTMActive } from './gtm';

export const getApplication = (state: { application: typeof fromJS }) => state.application || fromJS({});

export const getLocale = createSelector(getApplication, application => application.get('locale'));

const uniq = a => Array.from(new Set(a));
const lowerCase = map(s => s.trim().toLowerCase());
const includes = curry((collection, value) => _includes(value, collection));

const _getBrowserLocale = locales => {
    const allLocales = lowerCase(locales.toJS());
    const transform = flowRight(first, filter(includes(allLocales)), lowerCase, filter(identity), uniq);

    const { userLanguage, languages = [], language } = global.navigator;

    return transform([userLanguage, ...languages, language]);
};

const getBrowserLocale = createSelector(getLocales, locales => _getBrowserLocale(locales));

export const getUserLocale = createSelector(getLocale, getBrowserLocale, (locale, browserLocale) => {
    return locale || browserLocale;
});

export const getUserRegion = createSelector([getAllCookies, getUserLocale], (cookie, locale) => {
    const [, region] = locale.split('-');

    return cookie.get('user-region', '').toLowerCase() || region;
});

export const getToken = createSelector(getApplication, application => application.get('token'));

export const getLoginComplete = createSelector(getApplication, application => application.get('loginComplete'));

export const getIsLoggedIn = createSelector(
    getToken,
    getLoginComplete,
    (token, isLoginComplete) => !!(isLoginComplete && Boolean(token))
);

export const getShowHiveLiveModal = createSelector(getApplication, application => application.get('showHiveLiveModal'));

export const getIsLoggingIn = createSelector(getApplication, application => application.get('loggingIn'));

export const getIsLoggingOut = createSelector(getApplication, application => application.get('loggingOut'));

export const getLoggingInError = createSelector(getApplication, application => application.get('loggingInError'));

export const getHiveOffers = createSelector(getApplication, application => application.get('hive-offers'));

export const getHiveOffer = createSelector(getApplication, application =>
    application.getIn(['hive-offers', 'items', 0])
);

export const getHiveOfferDefaultUrl = createSelector(getApplication, application =>
    application.getIn(['hive-offers', 'defaultURL'])
);

export const getActiveHiveOffer = createSelector(
    getHiveOffer,
    getHiveOfferDefaultUrl,
    getConfigBound('features:shopMenuLink'),
    getAppSettings,
    (offer, defaultURL, shopMenuLinkEnabled, settings) => {
        if (shopMenuLinkEnabled && isGTMActive(offer)) {
            return offer
                .set('isOfferFirstTime', !settings.get(buildKey(SEEN_OFFER, offer.get('id'))))
                .set('link', offer.get('url') || defaultURL);
        }
    }
);

export const getWelcomeMessage = createSelector(getApplication, application => application.get('welcomeMessage'));

export const getShowWelcomeMessage = createSelector(getApplication, application =>
    application.get('showWelcomeMessage')
);

export const getShowLeakSensorModal = createSelector(getApplication, application =>
    application.get('showLeakSensorModal')
);

export const getShowHumidityControl = createSelector(getApplication, application =>
    application.get('showHumidityControl')
);

export const getShowLoginAnimation = createSelector(getApplication, application =>
    application.get('playLoginAnimation')
);

export const getActiveDiscoveryFeature = createSelector(
    getConfigBound('discoverNewFeatures'),
    (activeFeatures = ImmutableMap()) => activeFeatures.findKey(value => value) || ''
);

const mapFeatureToExtraConditions = (
    activeFeature,
    hubHasActions2Capability,
    hubHasGroupsCapability,
    bulbs,
    hueBridge,
    currentRoute,
    featureFlags
) => {
    const featuresWithExtraConditions = [DISCOVER_FEATURES.ACTIONS2, DISCOVER_FEATURES.GROUPS, DISCOVER_FEATURES.HUE];
    const functionMap = new Map([
        [!featuresWithExtraConditions.includes(activeFeature), true],
        [
            activeFeature === DISCOVER_FEATURES.ACTIONS2,
            featureFlags.get('newActions') && hubHasActions2Capability && currentRoute === dashboardRoute
        ],
        [
            activeFeature === DISCOVER_FEATURES.GROUPS,
            featureFlags.get('deviceGroups') &&
                bulbs &&
                bulbs.size > 1 &&
                hubHasGroupsCapability &&
                currentRoute === dashboardRoute
        ],
        [
            activeFeature === DISCOVER_FEATURES.HUE,
            featureFlags.get('hueSchedule') &&
                featureFlags.get('hueMimic') &&
                featureFlags.get('installPhilipsHue') &&
                !hueBridge
        ]
    ]);
    return functionMap.get(true);
};

export const getShowDiscoveryModal = createSelector(
    [
        state => getActiveDiscoveryFeature(state),
        state => getBulbs(state),
        state => getHueBridge(state),
        state => getHubHasCapability(state, capabilities.ACTIONS_2),
        state => getHubHasCapability(state, capabilities.PRODUCT_GROUP),
        state => getAppSetting(state, DISCOVER_NEW_FEATURES),
        (_, props: { currentRoute: string }) => props.currentRoute,
        state => getConfig(state, 'features')
    ],
    (
        activeFeature,
        bulbs,
        hueBridge,
        hubHasActions2Capability,
        hubHasGroupsCapability,
        discoverNewFeatures = ImmutableMap(),
        currentRoute,
        featureFlags
    ) => {
        const extraConditionMet = mapFeatureToExtraConditions(
            activeFeature,
            hubHasActions2Capability,
            hubHasGroupsCapability,
            bulbs,
            hueBridge,
            currentRoute,
            featureFlags
        );
        return !!extraConditionMet && !!activeFeature && !discoverNewFeatures.get(activeFeature);
    }
);

export const getContact = createSelector(
    [getConfigBound('help:supportEmail'), getConfigBound('help:deviceOfflineTelephoneNumber')],
    (email = '', contact = email) => contact
);

export const getHoneycombEndpoint = createSelector(getApplication, app => app.get('honeycombEndpoint'));
export const getBeekeeperEndpoint = createSelector(getApplication, app => app.get('beekeeperEndpoint'));



// WEBPACK FOOTER //
// ./src/core/state/application/selectors.js